import React, { useMemo, useState } from 'react';
import {
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
  Paper,
  Grid,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
} from '@mui/material';
import type { ProjectUnitOption } from '../../services/api';

const AVAILABILITY_OPTIONS = ['All', 'Available', 'Sold'] as const;
const BEDROOM_OPTIONS = [1, 2, 3, 4, 5] as const;

interface InputSectionProps {
  projects: string[];
  selectedProject: string;
  unitNumber: string;
  projectUnits: ProjectUnitOption[];
  projectUnitsLoading: boolean;
  onProjectChange: (value: string | null) => void;
  onUnitNumberChange: (value: string | null) => void;
}

const getUnitStatusTone = (status: string | null) => {
  if (!status) return 'rgba(var(--color-text-rgb), 0.4)';
  const normalized = status.toUpperCase();
  if (normalized === 'AVAILABLE') return 'var(--color-success)';
  if (normalized.includes('SOLD')) return 'var(--color-danger)';
  return 'var(--color-warning)';
};

const formatBedroomLabel = (bedroom: number | null) => {
  if (bedroom === null || bedroom === undefined) return null;
  if (bedroom === 0) return 'Studio';
  return `${bedroom} BR`;
};

const InputSection: React.FC<InputSectionProps> = ({
  projects,
  selectedProject,
  unitNumber,
  projectUnits,
  projectUnitsLoading,
  onProjectChange,
  onUnitNumberChange,
}) => {
  const theme = useTheme();
  const isWeb = useMediaQuery(theme.breakpoints.up('sm'));
  const isDarkMode = theme.palette.mode === 'dark';
  // Dark mode needs stronger hover so it's visible on dark grey background
  const hoverBg = isDarkMode ? 'rgba(var(--color-text-rgb), 0.14)' : 'rgba(var(--color-text-rgb), 0.08)';
  const selectedBg = isDarkMode ? 'rgba(var(--color-text-rgb), 0.08)' : 'rgba(var(--color-text-rgb), 0.05)';
  const focusedBg = isDarkMode ? 'rgba(var(--color-text-rgb), 0.12)' : 'rgba(var(--color-text-rgb), 0.06)';

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [availabilityFilter, setAvailabilityFilter] = useState<string>('All');
  const [bedroomFilter, setBedroomFilter] = useState<number[]>([]);

  const filteredUnits = useMemo(() => {
    let list = projectUnits;
    if (availabilityFilter !== 'All') {
      const want = availabilityFilter.toLowerCase();
      list = list.filter((u) => (u.status ?? '').toLowerCase() === want);
    }
    if (bedroomFilter.length > 0) {
      list = list.filter((u) => u.bedroom != null && bedroomFilter.includes(u.bedroom));
    }
    return list;
  }, [projectUnits, availabilityFilter, bedroomFilter]);

  const selectSx = {
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--color-text-rgb), 0.2)' },
    '& .MuiSelect-select': { color: 'var(--color-text-strong)' },
    bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
    '& .MuiInputLabel-root': { color: 'rgba(var(--color-text-rgb), 0.7)' },
  };

  return (
    <Paper
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        bgcolor: 'var(--color-surface)',
        boxShadow: '0 2px 8px rgba(var(--color-shadow-rgb), 0.3)',
      }}
    >
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm={5}>
          <Autocomplete
            fullWidth
            options={projects}
            value={selectedProject || null}
            onChange={(_, value) => onProjectChange(value)}
            slotProps={{
              popper: {
                sx: {
                  zIndex: 1800,
                },
              },
              paper: {
                sx: {
                  bgcolor: 'var(--color-surface)',
                  color: 'var(--color-text-strong)',
                  boxShadow: '0 4px 20px rgba(var(--color-shadow-rgb), 0.4)',
                  border: '1px solid rgba(var(--color-text-rgb), 0.08)',
                  '& .MuiAutocomplete-listbox': { bgcolor: 'transparent' },
                  '& .MuiAutocomplete-option': {
                    color: 'var(--color-text-strong)',
                    '&[aria-selected="true"]': { bgcolor: 'rgba(var(--color-accent-rgb), 0.2)' },
                    '&.Mui-focused': { bgcolor: 'rgba(var(--color-text-rgb), 0.08)' },
                  },
                },
              },
            }}
            ListboxProps={{
              sx: {
                maxHeight: 340,
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch',
              },
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Project Name"
                placeholder="Search project"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                    color: 'var(--color-text-strong)',
                    '& fieldset': {
                      borderColor: 'rgba(var(--color-text-rgb), 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(var(--color-text-rgb), 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--color-accent)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(var(--color-text-rgb), 0.7)',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'var(--color-accent)',
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box>
          <Autocomplete
            fullWidth
            options={filteredUnits}
            value={filteredUnits.find((unit) => unit.unitNumber === unitNumber) ?? projectUnits.find((unit) => unit.unitNumber === unitNumber) ?? null}
            loading={projectUnitsLoading}
            disabled={!selectedProject}
            onChange={(_, value) => onUnitNumberChange(value?.unitNumber ?? null)}
            slotProps={{
              popper: {
                sx: {
                  zIndex: 1800,
                },
              },
              paper: {
                sx: {
                  bgcolor: 'var(--color-surface)',
                  color: 'var(--color-text-strong)',
                  boxShadow: '0 4px 20px rgba(var(--color-shadow-rgb), 0.4)',
                  border: '1px solid rgba(var(--color-text-rgb), 0.08)',
                  '& .MuiAutocomplete-listbox': {
                    bgcolor: 'transparent',
                    maxHeight: 340,
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                    ...(isWeb
                      ? { p: 0 }
                      : { p: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }),
                  },
                  '& .MuiAutocomplete-option': {
                    color: 'var(--color-text-strong)',
                    ...(isWeb
                      ? {
                          py: 1.25,
                          px: 1.5,
                          borderRadius: 0,
                          borderBottom: '1px solid rgba(var(--color-text-rgb), 0.06)',
                          '&:last-of-type': { borderBottom: 'none' },
                          '&:hover': { bgcolor: hoverBg },
                          '&[aria-selected="true"]': { bgcolor: selectedBg },
                          '&.Mui-focused': { bgcolor: focusedBg },
                        }
                      : {
                          '&:hover': { bgcolor: hoverBg },
                          '&[aria-selected="true"]': { bgcolor: 'rgba(var(--color-accent-rgb), 0.2)' },
                          '&.Mui-focused': { bgcolor: focusedBg },
                        }),
                  },
                },
              },
            }}
            getOptionLabel={(option) => {
              const num = option.unitNumber ?? '';
              const size = option.areaSqft != null && option.areaSqft > 0
                ? ` · ${Number(option.areaSqft).toLocaleString()} sqft`
                : '';
              return num + size;
            }}
            isOptionEqualToValue={(option, value) => option.unitNumber === value.unitNumber}
            renderOption={(props, option) => {
              const bedroomLabel = formatBedroomLabel(option.bedroom);
              const statusLabel = option.status ? option.status.toUpperCase() : null;
              const statusTone = getUnitStatusTone(option.status ?? null);
              const showStatusText = statusLabel && statusLabel !== 'AVAILABLE';

              return (
                <Box
                  component="li"
                  {...props}
                  sx={[
                    {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      ...(isWeb
                        ? { px: 1.5, py: 1.25 }
                        : {
                            px: 1.25,
                            py: 1,
                            borderRadius: 1.5,
                            border: '1px solid rgba(var(--color-text-rgb), 0.1)',
                            bgcolor: 'rgba(var(--color-shadow-rgb), 0.25)',
                          }),
                    },
                    isWeb
                      ? {
                          '&:hover, &[aria-selected="true"]:hover': {
                            bgcolor: hoverBg,
                          },
                        }
                      : {
                          '&:hover, &[aria-selected="true"]:hover': {
                            bgcolor: isDarkMode ? 'rgba(var(--color-text-rgb), 0.2)' : 'rgba(var(--color-text-rgb), 0.18)',
                          },
                        },
                  ]}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      flexShrink: 0,
                      borderRadius: '50%',
                      bgcolor: statusTone,
                      ...(isWeb ? {} : { boxShadow: `0 0 6px ${statusTone}` }),
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'var(--color-text-strong)',
                      fontWeight: isWeb ? 500 : 700,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {option.unitNumber}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {showStatusText && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'var(--color-danger)',
                          fontWeight: 600,
                          letterSpacing: '0.04em',
                        }}
                      >
                        {statusLabel}
                      </Typography>
                    )}
                    {option.areaSqft != null && option.areaSqft > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(var(--color-text-rgb), 0.75)',
                          fontSize: '0.75rem',
                        }}
                      >
                        {Number(option.areaSqft).toLocaleString()} sqft
                      </Typography>
                    )}
                    {bedroomLabel && (
                      <Box
                        sx={{
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 1.25,
                          bgcolor: 'rgba(var(--color-text-rgb), 0.12)',
                          color: 'rgba(var(--color-text-rgb), 0.85)',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                        }}
                      >
                        {bedroomLabel}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Unit Number"
                placeholder={
                  selectedProject
                    ? isWeb
                      ? 'Select Unit'
                      : 'Search unit'
                    : 'Select a project first'
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                    color: 'var(--color-text-strong)',
                    '& fieldset': {
                      borderColor: 'rgba(var(--color-text-rgb), 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(var(--color-text-rgb), 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--color-accent)',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(var(--color-text-rgb), 0.7)',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'var(--color-accent)',
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {projectUnitsLoading ? <CircularProgress size={20} sx={{ color: 'var(--color-text-strong)' }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {selectedProject && (
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => setFilterDialogOpen(true)}
              sx={{
                mt: 0.75,
                display: 'inline-block',
                color: 'var(--color-accent)',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Filter
            </Link>
          )}
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
            border: '1px solid rgba(var(--color-text-rgb), 0.08)',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ color: 'var(--color-text-strong)', borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)', pb: 1.5, pt: 2 }}>
          Filter units
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1, overflow: 'visible' }}>
          <FormControl fullWidth size="small" sx={{ ...selectSx, mb: 2, mt: 0.5 }}>
            <InputLabel id="filter-availability-label" shrink>Availability</InputLabel>
            <Select
              labelId="filter-availability-label"
              label="Availability"
              value={availabilityFilter}
              onChange={(e) => setAvailabilityFilter(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: 'var(--color-surface)',
                    border: '1px solid rgba(var(--color-text-rgb), 0.08)',
                    '& .MuiMenuItem-root': { color: 'var(--color-text-strong)' },
                  },
                },
              }}
            >
              {AVAILABILITY_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={selectSx}>
            <InputLabel id="filter-bedroom-label" shrink>Bedroom</InputLabel>
            <Select
              multiple
              labelId="filter-bedroom-label"
              label="Bedroom"
              value={bedroomFilter}
              onChange={(e) => {
                const v = e.target.value;
                setBedroomFilter(typeof v === 'string' ? [] : v);
              }}
              renderValue={(selected) =>
                selected.length === 0 ? 'All' : [...(selected as number[])].sort((a, b) => a - b).map((n) => formatBedroomLabel(n)).join(', ')
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: 'var(--color-surface)',
                    border: '1px solid rgba(var(--color-text-rgb), 0.08)',
                    '& .MuiMenuItem-root': { color: 'var(--color-text-strong)' },
                  },
                },
              }}
            >
              {BEDROOM_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n} BR
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
          <Button
            onClick={() => setFilterDialogOpen(false)}
            sx={{ color: 'var(--color-accent)' }}
          >
            Filter
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default InputSection;
